import {
  AddIcon,
  Button,
  Col,
  DeleteIcon,
  EditIcon,
  ListTable,
  Modal,
  Row,
  Search,
  Spinner,
} from 'elmo-elements';
import React, { ReactElement, useEffect, useState } from 'react';
import '../settings.scss';
import {
  useCreateThemeMutation,
  useDeleteThemeMutation,
  useGetThemesQuery,
} from '@/modules/services';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import SettingsThemeSelectedRoutes from '@/pages/Themes/themeRoute';
import { ThemeDataType } from '@/modules/types/theme';
import { defaultThemeValue } from '@/pages/Themes';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { useDispatch } from 'react-redux';
import { getQueryString, getValidPage, pushParamsToUrl } from '@/lib/util';
import { findIndex } from 'lodash';
import { appData } from '@/lib/data';
import './index.scss';

const ThemeComponent = (): ReactElement => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [deleteThemeId, setDeleteThemeId] = useState<string>('');
  const currentQueryString = getQueryString(
    location.search,
    appData.pagination.defaultParams,
  );
  const { sort, order } = currentQueryString;
  const pageSizes = appData.pagination.pages;
  const sortableColumns = [
    { key: 'name', name: 'Theme name' },
    { key: 'created_at', name: 'Created' },
  ];
  const sortedColumnIndex = findIndex(sortableColumns, { key: sort });

  const { data: themeData } = useGetThemesQuery(currentQueryString);
  const [deleteTheme, deleteThemeStatus] = useDeleteThemeMutation();
  const [createTheme, createThemeStatus] = useCreateThemeMutation();

  const handleDeleteTheme = (themeId: string) => {
    if (!deleteThemeId) return;

    deleteTheme(themeId);
    setDeleteThemeId('');
    setIsModalOpen(false);
  };

  const handleSort = (column: number, direction: string) => {
    pushParamsToUrl(
      {
        sort: sortableColumns[column].key,
        order: direction.toUpperCase(),
        page: 1,
      },
      history,
    );
  };

  const handlSearch = (search: string) => {
    pushParamsToUrl(
      {
        search: searchKeyword,
        page: 1,
      },
      history,
    );
  };

  const handleSearchChange = (searchValue: string) => {
    setSearchKeyword(searchValue);
    if (searchValue === '') pushParamsToUrl({ search: '', page: 1 }, history);
  };

  const getFormatedDate = (dateString: string) => {
    const date = new Date(dateString);
    const month = date.toLocaleString('en-AU', { month: 'short' });

    return `${date.getDate()} ${month} ${date.getFullYear()}`;
  };

  const renderActions = (data: ThemeDataType, index: number) => [
    {
      icon: <EditIcon />,
      label: 'Edit',
      onClick: () => {
        history.push(
          generatePath(SettingsThemeSelectedRoutes.path, {
            themeId: data.id,
          }),
        );
      },
    },
    {
      icon: <DeleteIcon />,
      label: 'Remove',
      onClick: () => {
        setDeleteThemeId(data.id);
        setIsModalOpen(true);
      },
    },
  ];

  useEffect(() => {
    if (!createThemeStatus.isError && !createThemeStatus.isSuccess) return;

    let message = 'Create new theme error! Something went wrong.';
    if (createThemeStatus.isSuccess) {
      message = 'Create new theme successful.';
    }

    dispatch({
      type: flashMessageActions.addFlashMessage,
      payload: {
        isActive: true,
        message,
      },
    });

    /* Redirect to edit form after successful creation */
    if (createThemeStatus.isLoading || !createThemeStatus.data) return;
    history.push(
      generatePath(SettingsThemeSelectedRoutes.path, {
        themeId: createThemeStatus.data.id,
      }),
    );
  }, [createThemeStatus]);

  useEffect(() => {
    if (!deleteThemeStatus.isError && !deleteThemeStatus.isSuccess) return;

    let message =
      'Cannot delete the theme, it is used by some of your courses or is set as the default theme.';
    if (deleteThemeStatus.isSuccess) {
      message = 'Delete theme successful.';
    }

    dispatch({
      type: flashMessageActions.addFlashMessage,
      payload: {
        isActive: true,
        message,
      },
    });
  }, [deleteThemeStatus]);

  if (!themeData) return <Spinner />;

  return (
    <>
      <Modal
        id="ModalDialog"
        type="medium"
        isOpened={isModalOpen}
        closeModal={() => {
          setDeleteThemeId('');
          setIsModalOpen(false);
        }}
        primaryButton={
          <Button
            type="danger"
            isBlock={true}
            onClick={() => handleDeleteTheme(deleteThemeId)}
          >
            Continue
          </Button>
        }
        title="Delete theme"
      >
        Access to this theme will be lost once it is delete. This action cannot
        be undone.
      </Modal>
      <Row className="theme-header">
        <Col span={12}>
          <Search
            value={searchKeyword}
            isVisible
            onChange={(value) => {
              handleSearchChange(value);
            }}
            onSubmit={() => {
              handlSearch(searchKeyword);
            }}
            isSubmitEnabledAlways={true}
          />
        </Col>
        <Col span={3}>
          <Button
            icon={<AddIcon />}
            isDisabled={createThemeStatus.isLoading}
            onClick={() => {
              createTheme(defaultThemeValue);
            }}
            id="add-new-button"
          >
            Add new
          </Button>
        </Col>
      </Row>
      <ListTable
        ariaLabel="List of Themes"
        sortColumn={sortedColumnIndex}
        sortDirection={order.toLowerCase()}
        onSort={handleSort}
      >
        <ListTable.Header>
          <ListTable.Column label="Company name" sortable={true} />
          <ListTable.Column label="Created" sortable={true} />
        </ListTable.Header>
        <ListTable.Body>
          {themeData.data.map((theme: ThemeDataType, index) => (
            <ListTable.Tr key={theme.id} actions={renderActions(theme, index)}>
              <ListTable.Td>{theme.name}</ListTable.Td>
              <ListTable.Td>{getFormatedDate(theme.createdAt)}</ListTable.Td>
            </ListTable.Tr>
          ))}
        </ListTable.Body>
        <ListTable.Pagination
          pageSizeOptions={pageSizes}
          pageSize={themeData?.meta.limit || pageSizes[0]}
          currentPage={themeData?.meta.page || 1}
          onPageChange={(pageNumber) => {
            pushParamsToUrl({ page: pageNumber }, history);
          }}
          onPageSizeChange={(pageSize) => {
            const pageNumber = getValidPage(
              themeData?.meta.limit || pageSizes[0],
              pageSize,
              themeData?.meta.page || 1,
              themeData?.meta.itemCount,
            );

            pushParamsToUrl(
              {
                limit: pageSize,
                page: pageNumber,
              },
              history,
            );
          }}
          totalResults={themeData.meta.itemCount}
        />
      </ListTable>
    </>
  );
};
export default ThemeComponent;
